import { Card, Col, Row } from "react-bootstrap";
import HippoTable from "components/ReactTable/HipposReactTable";
import { useContext, useEffect, useState } from "react";
import { apiService } from "service/ApiService";
import StoreContext from "contexts/StoreContext";
import TimestampColumn from "components/Custom/TimestampColumn";
import ExportToExcelButton from "views/Components/ExportToExcelButton";
import { exportToExcel } from "utils/AppUtils";
import DateRangeContext from "contexts/DateRangeContext";
import { serverTimesampToNormalDate } from "utils/AppUtils";
import { ClipLoader } from "react-spinners";

import { useTranslation } from "react-i18next";

export default function CancelledAndDestroyedTable() {
  const { t } = useTranslation();

  const { storeId, setStoreId } = useContext(StoreContext);
  const { dateRange, setDateRange } = useContext(DateRangeContext);
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [excelLoading, setExcelLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(true);

  const columns = [
    {
      Header: "ID ",
      accessor: "localId",
      maxWidth: 100,
      minWidth: 50,
      width: 100,
    },
    {
      Header: t("tables.headers.product"),
      accessor: "product",
      maxWidth: 100,
      minWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.mode"),
      accessor: "mode",
      maxWidth: 100,
      minWidth: 50,
      width: 100,
    },

    {
      Header: t("tables.headers.table"),
      accessor: "table",
      maxWidth: 100,
      minWidth: 50,
      width: 100,
    },
    {
      Header: t("tables.headers.opening"),
      accessor: "subtotal",
      disableFilters: true,
      minWidth: 100,
      maxWidth: 150,
      width: 100,
    },
    {
      Header: t("tables.headers.discount"),
      accessor: "discount",
      disableFilters: true,
      minWidth: 100,
      maxWidth: 100,
      width: 100,
    },
    {
      Header: t("tables.headers.totalEuro"),
      accessor: "total",
      disableFilters: true,
      minWidth: 100,
      maxWidth: 100,
      width: 100,
    },
    {
      Header: t("tables.headers.enteredBy"),
      accessor: "storeUser",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.enteredAt"),
      accessor: "createdAt",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.closedBy"),
      accessor: "closedByUser",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.closedAt"),
      accessor: "closedAt",
      minWidth: 200,
      maxWidth: 300,
      width: 200,
    },
    {
      Header: t("tables.headers.status"),
      accessor: "state",
      disableFilters: true,
      minWidth: 90,
      maxWidth: 90,
      width: 90,
    },
  ];

  const getData = () => {
    setShowLoader(true);
    apiService
      .post(`api/v1/orders/store/${storeId}/cancelled-discounted`, {
        fromDate: dateRange[0],
        toDate: dateRange[1],
      })
      .then((resp) => {
        const allOrders = resp.cancelled.concat(resp.discounted);
        const finalOrders = _.sortBy(allOrders, "id");
        setRawData(_.cloneDeep(finalOrders));
        setData(attachCustom(finalOrders));
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const attachCustom = (data) => {
    const enhanced = data.map((el) => {
      el.product = el.product.name;
      el.storeUser = _.get(el, "order.storeUser.name", "");
      el.status = !el.cancelledByUser
        ? t("tables.headers.discount")
        : el.destroyed
          ? t("tables.headers.wastageSingular")
          : t("tables.headers.cancellation");
      el.mode =
        el.order.modeId == 1 ? (
          <i title="Takeaway" className="nc-icon nc-bag text-success icon-bold"></i>
        ) : el.order.modeId == 2 ? (
          <i title="Delivery" className="fa fa-bicycle text-danger"></i>
        ) : (
          <i title="Restaurant" className="nc-icon nc-grid-45 text-warning icon-bold"></i>
        );
      el.orderId = el.order.localId;
      el.table = el.order.table ? el.order.table.name : null;
      el.subtotal = el.subTotal.toFixed(2);
      el.discount = el.discount.toFixed(2);
      el.total = el.total.toFixed(2);
      el.closedAt = <TimestampColumn timestamp={el.cancelledAt ? el.cancelledAt : el.payedAt} />;
      el.createdAt = <TimestampColumn timestamp={el.createdAt} />;
      el.closedByUser = el.cancelledByUser ? _.get(el, "cancelledByUser.name", "") : _.get(el, "payedByUser.name", "");
      el.state = !el.cancelledByUser ? (
        <i className="nc-icon icon-bold nc-scissors text-info" title={t("common.labels.discount")}></i>
      ) : el.destroyed ? (
        <i className="nc-icon icon-bold nc-simple-remove text-danger" title={t("common.labels.wastageSingular")}></i>
      ) : (
        <i className="nc-icon icon-bold nc-simple-delete text-warning" title={t("common.labels.cancellation")}></i>
      );
      return el;
    });

    return enhanced;
  };

  const formatData = (data) => {
    if (data.length === 0) return [];
    let formattedData = data.map((row) => {
      return {
        [t("tables.headers.product")]: row.product.name,
        [t("tables.headers.mode")]: row.modeId == 1 ? "Take Away" : row.modeId == 2 ? "Delivery" : "Restaurant",
        [t("tables.headers.orderId")]: row.order.localId,
        [t("tables.headers.table")]: _.get(row, "order.table.name", ""),
        [t("tables.headers.subtotalEuro")]: row.subTotal,
        [t("tables.headers.discount")]: row.discount,
        [t("tables.headers.totalEuro")]: row.total,
        [t("tables.headers.enteredBy")]: row.order.storeUser.name,
        [t("tables.headers.enteredAt")]: serverTimesampToNormalDate(row.createdAt),
        [t("tables.headers.closedBy")]: row.cancelledByUser ? _.get(row, "cancelledByUser.name", "") : _.get(row, "payedByUser.name", ""),
        [t("tables.headers.closedAt")]: row.cancelledAt ? serverTimesampToNormalDate(row.cancelledAt) : serverTimesampToNormalDate(row.payedAt),
        [t("tables.headers.status")]: !row.cancelledByUser
          ? t("tables.headers.discount")
          : row.destroyed
            ? t("tables.headers.wastageSingular")
            : t("tables.headers.cancellation"),
      };
    });
    return formattedData;
  };

  const handleExportData = () => {
    setExcelLoading(true);
    exportToExcel(formatData(rawData), `cancellations-${dateRange[0]}-to-${dateRange[1]}`, t("common.labels.discountsAndCancellations"));
    setExcelLoading(false);
  };

  useEffect(() => {
    if (!storeId || !dateRange) return;
    getData();
  }, [storeId, dateRange]);

  return (
    <>
      <Card className="strpied-tabled-with-hover">
        <Card.Header>
          <Row>
            <Col>
              <Card.Title as="h4">{t("reports.discountsAndCancellationsAmpersand")}</Card.Title>
            </Col>
          </Row>
          <Row>
            <Col md="10">
              <p className="card-category">
                {t("messages.showsDiscountsAndCancellationsMadeDuringThePeriodDefinedByTheSelectedDatesAnd")} {t("common.labels.times")}
              </p>
            </Col>
            <Col md="2">{data.length === 0 ? "" : <ExportToExcelButton handler={handleExportData} disabled={excelLoading} />}</Col>
          </Row>
        </Card.Header>
        <Card.Body className="table-responsive p-0">
          {showLoader ? (
            <div style={{ textAlign: "center", marginTop: "5vh", marginBottom: "5vh" }}>
              <ClipLoader size={50} color={"#ff6fbc"} />
            </div>
          ) : (
            <Col>
              <HippoTable data={data} columns={columns} className="-striped -highlight primary-pagination" />
            </Col>
          )}
        </Card.Body>
        <Card.Footer>
          <div className="mb-1"></div>
        </Card.Footer>
      </Card>
    </>
  );
}
